import { trackError } from "@/lib/errorTracking";
import {
  getAppVersionTelemetry,
  getAvatarTelemetry,
  getBrowserNetworkDiagnostics,
  getBrowserSessionDiagnostics,
  getErrorTelemetry,
  getServerUrlHost,
} from "@/lib/livekit-agent-telemetry";
import type { SessionMode } from "@/lib/sessionRouting";

type PostHogLike = {
  capture: (eventName: string, properties?: Record<string, unknown>) => void;
};

type ConnectionEventDetails = {
  roomName?: string | null;
  tokenSource?: string | null;
  serverUrl?: string | null;
};

export type BuildLiveKitConnectionEventPayloadInput = {
  error: unknown;
  selectedLanguageCode: string;
  selectedLanguageLevel: string;
  userType: "authenticated" | "guest";
  sessionMode: SessionMode;
  details?: ConnectionEventDetails | null;
  traceId?: string | null;
  phase: string;
  spatiusPrimary: boolean;
  selectedVoice: string;
  avatarId?: string;
  noAvatar: boolean;
  extra?: Record<string, unknown>;
};

export type LiveKitConnectionFailureCategory =
  | "avatar_viewer_bootstrap_failed"
  | "http_fetch_failed"
  | "http_status_failed"
  | "livekit_cancelled"
  | "livekit_peer_connection_failed"
  | "microphone_negotiation_timeout"
  | "microphone_permission_or_device"
  | "timeout"
  | "timeout_guard_disconnect"
  | "unknown";

const SEVERE_HANDLED_FAILURE_EVENTS = new Set([
  "connection_details_fetch_failed",
  "connection_details_http_failed",
  "livekit_room_connect_failed",
  "livekit_connection_failed",
  "microphone_enable_failed",
]);

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function isMicrophonePermissionError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  const errorName = error instanceof Error ? error.name.toLowerCase() : "";
  return (
    errorName === "notallowederror" ||
    message.includes("notallowederror") ||
    message.includes("permission denied") ||
    message.includes("permission dismissed") ||
    message.includes("not allowed by the user agent")
  );
}

function isTimeoutGuardDisconnect(extra?: Record<string, unknown>): boolean {
  return (
    extra?.connect_cancel_origin === "timeout_guard" || extra?.timeout_guard_disconnect === true
  );
}

function getConnectionErrorTelemetry({
  error,
  failureCategory,
}: {
  error: unknown;
  failureCategory: LiveKitConnectionFailureCategory;
}) {
  const errorTelemetry = getErrorTelemetry(error);

  if (failureCategory !== "timeout_guard_disconnect") {
    return errorTelemetry;
  }

  return {
    ...errorTelemetry,
    error: "Timeout guard initiated disconnect",
    raw_error: errorTelemetry.error,
  };
}

export function classifyLiveKitConnectionFailure({
  eventName,
  error,
  extra,
}: {
  eventName: string;
  error: unknown;
  extra?: Record<string, unknown>;
}): LiveKitConnectionFailureCategory {
  const message = getErrorMessage(error).toLowerCase();
  const errorName = error instanceof Error ? error.name.toLowerCase() : "";

  if (isTimeoutGuardDisconnect(extra)) {
    return "timeout_guard_disconnect";
  }
  if (eventName === "spatius_viewer_connect_failed") {
    return "avatar_viewer_bootstrap_failed";
  }
  if (eventName === "connection_details_http_failed" || extra?.status) {
    return "http_status_failed";
  }
  if (
    eventName === "connection_details_fetch_failed" ||
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("load failed")
  ) {
    return "http_fetch_failed";
  }
  if (message.includes("could not establish pc connection") || message.includes("ice connection")) {
    return "livekit_peer_connection_failed";
  }
  if (message.includes("negotiation timed out")) {
    return "microphone_negotiation_timeout";
  }
  if (
    errorName.includes("notallowed") ||
    errorName.includes("notfound") ||
    message.includes("permission denied") ||
    message.includes("permission dismissed") ||
    message.includes("requested device not found")
  ) {
    return "microphone_permission_or_device";
  }
  if (message.includes("cancel") || message.includes("abort")) {
    return "livekit_cancelled";
  }
  if (message.includes("timed out") || message.includes("timeout")) {
    return "timeout";
  }
  return "unknown";
}

export function shouldCaptureLiveKitHandledException({
  eventName,
  failureCategory,
  extra,
}: {
  eventName: string;
  failureCategory: LiveKitConnectionFailureCategory;
  extra?: Record<string, unknown>;
}): boolean {
  if (extra?.ignored_after_timeout_guard) {
    return false;
  }
  if (extra?.handled_exception_reported) {
    return false;
  }
  if (!SEVERE_HANDLED_FAILURE_EVENTS.has(eventName)) {
    return false;
  }
  return (
    failureCategory !== "livekit_cancelled" &&
    failureCategory !== "microphone_permission_or_device" &&
    failureCategory !== "timeout_guard_disconnect"
  );
}

export function buildLiveKitConnectionEventPayload({
  error,
  selectedLanguageCode,
  selectedLanguageLevel,
  userType,
  sessionMode,
  details,
  traceId,
  phase,
  spatiusPrimary,
  selectedVoice,
  avatarId,
  noAvatar,
  extra,
  eventName,
}: BuildLiveKitConnectionEventPayloadInput & { eventName?: string }): Record<string, unknown> {
  const failureCategory = classifyLiveKitConnectionFailure({
    eventName: eventName || "unknown",
    error,
    extra,
  });

  return {
    ...getConnectionErrorTelemetry({ error, failureCategory }),
    ...getAppVersionTelemetry(),
    ...getBrowserSessionDiagnostics(),
    ...getBrowserNetworkDiagnostics(),
    failure_category: failureCategory,
    language_code: selectedLanguageCode,
    language_level: selectedLanguageLevel,
    user_type: userType,
    session_mode: sessionMode,
    room_name: details?.roomName || null,
    token_source: details?.tokenSource || null,
    server_url_host: getServerUrlHost(details?.serverUrl),
    trace_id: traceId || null,
    phase,
    spatius_primary: spatiusPrimary,
    no_avatar: noAvatar,
    ...getAvatarTelemetry({ selectedVoice, avatarId, noAvatar }),
    ...extra,
  };
}

export type CaptureLiveKitConnectionEventInput = BuildLiveKitConnectionEventPayloadInput & {
  eventName: string;
  posthogClient?: PostHogLike | null;
  hasPosthogKey: boolean;
};

export function captureLiveKitConnectionEvent({
  eventName,
  posthogClient,
  hasPosthogKey,
  ...payloadInput
}: CaptureLiveKitConnectionEventInput) {
  if (!posthogClient || !hasPosthogKey) {
    return false;
  }

  const payload = buildLiveKitConnectionEventPayload({ ...payloadInput, eventName });
  posthogClient.capture(eventName, payload);

  const failureCategory = payload.failure_category as LiveKitConnectionFailureCategory;
  if (
    shouldCaptureLiveKitHandledException({
      eventName,
      failureCategory,
      extra: payloadInput.extra,
    })
  ) {
    trackError(payloadInput.error, {
      ...payload,
      context: eventName,
      error_context: "handled_livekit_connection_failure",
      handled: true,
    });
    return true;
  }

  return false;
}
